import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { NeighborhoodServicesBoard } from '@api/BoardSDK';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';

export const SERVICE_STATUSES = ['Not Contacted', 'Called', 'Emailed', 'Interested', 'Booked Ad', 'Not Interested', 'Bad Contact'] as const;
const COLS = ['contactName', 'email', 'phone', 'website', 'address', 'serviceType', 'paper', 'outreachStatus', 'lastContacted', 'nextFollowup', 'notes'] as const;

export const getServiceProspects = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), q: z.string().optional(), cursor: z.string().optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new NeighborhoodServicesBoard();
      const first = () => b.items().withColumns([...COLS]).where(data.q?.trim() ? { name: { contains: data.q.trim() } } : {}).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 25 }).execute();
      if (!data.cursor) return first();
      try { return await b.items().withColumns([...COLS]).withPagination({ limit: 25, cursor: data.cursor }).execute(); }
      catch (e) { if (/cursor/i.test(String((e as Error)?.message))) return first(); throw e; }
    });
  });

/** All prospects (the list is small), for showing under category headings. Optional server-side name search. */
export const getAllServiceProspects = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), q: z.string().optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new NeighborhoodServicesBoard();
      let page = await b.items().withColumns([...COLS]).where(data.q?.trim() ? { name: { contains: data.q.trim() } } : {}).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 100 }).execute();
      const items = [...page.items];
      for (let i = 0; page.cursor && i < 20; i++) { page = await b.items().withColumns([...COLS]).withPagination({ limit: 100, cursor: page.cursor }).execute(); items.push(...page.items); }
      return { items: items.filter((i) => !i.name.startsWith(DEL)) };
    });
  });

export const setServiceStatus = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().regex(/^\d+$/), status: z.enum(SERVICE_STATUSES) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const day = new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + 'T12:00:00Z');
    await withBoardAuth(() => new NeighborhoodServicesBoard().item(data.id).update({ outreachStatus: data.status, ...(data.status !== 'Not Contacted' ? { lastContacted: day } : {}) }).execute());
    return { ok: true };
  });

const DEL = '[Deleted] ';
export const SERVICE_TYPES = ['Construction / Contractor', 'Plumbing', 'Electrical', 'Handyman', 'Cleaning', 'Moving & Storage', 'Landscaping', 'Pest Control', 'Other'] as const;

/** Edit a prospect's details. Blank email clears it. */
export const updateServiceProspect = createServerFn({ method: 'POST' })
  .validator(z.object({
    pass: z.string(), id: z.string().regex(/^\d+$/), name: z.string().trim().min(1).max(200),
    contactName: z.string().max(200), address: z.string().max(500), phone: z.string().max(100), email: z.string().max(200), website: z.string().max(300),
    serviceType: z.enum(SERVICE_TYPES), notes: z.string().max(5000),
  }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const em = data.email.trim();
    await withBoardAuth(() => new NeighborhoodServicesBoard().item(data.id).update({
      name: data.name, contactName: data.contactName.trim(), address: data.address.trim(), phone: data.phone.trim(), website: data.website.trim(),
      serviceType: data.serviceType, notes: data.notes, email: em ? { email: em, label: em } : null,
    }).execute());
    return { ok: true };
  });

/** Remove a prospect: archive on monday; if the app isn't allowed to, tag it so the list hides it. */
export const deleteServiceProspect = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().regex(/^\d+$/), name: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    return withBoardAuth(async () => {
      const b = new NeighborhoodServicesBoard();
      try { await b.item(data.id).archive().execute(); return { ok: true }; }
      catch (e) { console.warn('[services] archive not allowed, tagging deleted', e); }
      await b.item(data.id).update({ name: DEL + data.name.replace(DEL, '') }).execute();
      return { ok: true };
    });
  });
